import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  InputLeftElement,
  InputRightElement,
  ScaleFade,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import useQuote from "../../hooks/useQuote";
import { selectStates } from "../../lib/models/short-term/short-term-form-selects.model";
import { QuoteType } from "../../lib/types/QuoteType";
import CalculationErrors from "../common-quote/FormCalculationErrors";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import SelectField from "../form-fields/SelectField";
import TextInputField from "../form-fields/TextInputField";

function RentalPropertySummaryForm({ quoteType }: { quoteType: QuoteType }) {
  const { values } = useFormikContext<any>();
  const { dispatch } = useQuote();

  const [isSectionValid, setIsSectionValid] = React.useState<boolean>(false);
  const [sectionWarnings, SetSectionWarnings] = React.useState<Array<string>>(
    []
  );

  const propertyScenarioKeys = React.useMemo(() => {
    return [
      "mortgagePayoffs",
      "originalPurchasePrice",
      "appraisedVal3",
      "purchasePrice",
      "mortgagePayoffs2",
    ];
  }, []);

  const validatePropertyScenarios = React.useCallback(() => {
    let valid = false;

    for (let key of propertyScenarioKeys) {
      if (values[key] !== 0) {
        valid = true;
        break;
      }
    }

    setIsSectionValid(valid);
  }, [values, propertyScenarioKeys]);

  React.useEffect(() => {
    validatePropertyScenarios();
  }, [values, validatePropertyScenarios]);

  const setFormWarnings = React.useCallback(
    (isValid: boolean) => {
      if (isValid) {
        dispatch({
          type: "QUOTE_FORM_ERROR",
          payload: false,
        });

        SetSectionWarnings([]);
      } else {
        dispatch({
          type: "QUOTE_FORM_ERROR",
          payload: true,
        });

        SetSectionWarnings(["All property values should not be $0"]);
      }
    },
    [dispatch]
  );

  React.useEffect(() => {
    setFormWarnings(isSectionValid);
  }, [isSectionValid, setFormWarnings]);

  return (
    <Grid
      templateColumns="repeat(12, minmax(0, 1fr))"
      gap={{ base: 6, md: 8, lg: 8 }}
    >
      <GridItem colSpan={12}>
        <Flex
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <FormSectionHeader id="property-summary" label="Property Summary" />
            <Tooltip
              label={
                "Enter in the values of the properties according to the respective time your customer has owned them.  If purchasing, place the value in the purchase section only."
              }
              fontSize="sm"
            >
              <span>
                <InfoOutlineIcon marginLeft={3} w={3} h={3} />
              </span>
            </Tooltip>
          </Box>
          <Box marginTop={[4, 0, 0]} marginBottom={[4, 0, 0]}>
            <ScaleFade initialScale={0.9} in={!!sectionWarnings.length}>
              <CalculationErrors
                label="Calculation Warnings"
                calculationErrors={sectionWarnings}
              />
            </ScaleFade>
          </Box>
        </Flex>
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }} alignSelf="center">
        <Text
          marginTop={{ base: 0, lg: 7 }}
          fontSize="sm"
          fontWeight="medium"
          textAlign={{ base: "left", lg: "right" }}
        >
          {quoteType === "Rental" ? "Properties Owned < 3 Months" : ""}
        </Text>
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Original Purchase Price"
          type="number"
          name="originalPurchasePrice"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Mortgage Payoffs"
          type="number"
          name="mortgagePayoffs"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }} alignSelf="center">
        <Text
          marginTop={{ base: 0, lg: 7 }}
          fontSize="sm"
          fontWeight="medium"
          textAlign={{ base: "left", lg: "right" }}
        >
          {quoteType === "Rental"
            ? "Properties Owned > 3 Months"
            : "Properties Owned > 12 Months"}
        </Text>
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Appraised Value"
          type="number"
          name="appraisedVal3"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Mortgage Payoffs"
          type="number"
          name="mortgagePayoffs2"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }} alignSelf="center">
        <Text
          marginTop={{ base: 0, lg: 7 }}
          fontSize="sm"
          fontWeight="medium"
          textAlign={{ base: "left", lg: "right" }}
        >
          Purchases
        </Text>
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 8 }}>
        <TextInputField
          label="Purchase Price"
          type="number"
          name="purchasePrice"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Renovation Costs After Purchase"
          type="number"
          name="renovationCostsAfterPurchase"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Monthly Rent"
          type="number"
          name="monthlyRent"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Annual Insurance"
          type="number"
          name="annualInsurance"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Annual Flood Insurance"
          type="number"
          name="annualFloodInsurance"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Annual Taxes"
          type="number"
          name="annualTaxes"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 4 }}>
        <TextInputField
          label="Annual HOA"
          type="number"
          name="annualHOA"
          formatNumber
          children={<InputLeftElement children="$" />}
        />
      </GridItem>

      {quoteType === "Premier" && (
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <TextInputField
            label="Property Management Fee"
            type="number"
            name="propertyManagementFee"
            children={<InputRightElement children="%" />}
          />
        </GridItem>
      )}

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Original Purchase Date"
          name="originalPurchaseDate"
          type="date"
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField
          label="Target Close Date"
          name="targetCloseDate"
          type="date"
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 12 }}>
        <TextInputField label="Street Address" name="propertyAddress" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <TextInputField label="City" name="city" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 3 }}>
        <SelectField
          label="State"
          name="state"
          data={selectStates}
          placeholder="Select a state"
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 3 }}>
        <TextInputField
          label="Zipcode"
          name="zip"
          type="number"
          formatNumber
          formatOptions={{
            format: "#####",
            thousandSeparator: false,
            allowLeadingZeros: true,
          }}
        />
      </GridItem>
    </Grid>
  );
}

export default RentalPropertySummaryForm;
